import * as Yup from "yup"

import { IAddress, ITelecom } from "models"
import { FhirAddress } from "commons"

import { TelecomErrorType } from "./types"

const genders = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
]

const EMAIL_CONTACT_POINT_SYSTEM = "email"
const PHONE_CONTACT_POINT_SYSTEM = "phone"

const getEmails = (telecom: ITelecom[]) => telecom?.filter(({ system }) => system === EMAIL_CONTACT_POINT_SYSTEM)

const getPhones = (telecom: ITelecom[]) => telecom?.filter(({ system }) => system === PHONE_CONTACT_POINT_SYSTEM)

const emailValidation = (value: string) => {
  const check = { isValid: true, msg: "" }

  if (!value) {
    check.isValid = false
    check.msg = "Email address is required"
  } else {
    if (
      !/^([a-zA-Z0-9]([_]*|[.+][a-zA-Z0-9]+|[-]+[a-zA-Z0-9]+)*)+@[a-zA-Z0-9]+([.|-]{0,1}[a-zA-Z0-9])*(\.[a-zA-Z]{2,})+$/.test(
        value,
      )
    ) {
      check.isValid = false
      check.msg = "Invalid email address"
    }
  }

  return check
}

const getInitialTelecomErrors = (emails: ITelecom[], phones: ITelecom[]) => ({
  emails: emails.map<TelecomErrorType>((email) => {
    if (!email.value) {
      return { hasError: true, msg: "Email address is required", isTouched: false }
    }

    if (!emailValidation(email.value)) {
      return { hasError: true, msg: "Wrong email", isTouched: false }
    }

    return { hasError: false, msg: "", isTouched: false }
  }),
  phones: phones.map<TelecomErrorType>((phone) => {
    if (!phone.value) {
      return { hasError: true, msg: "Phone number is required", isTouched: false }
    }

    return { hasError: false, msg: "", isTouched: false }
  }),
})

const getStringAddress = (address?: IAddress) => {
  if (!address) {
    return "Unspecified address"
  }

  const { line1, line2, city, state, country, postalCode } = address ?? {}

  return Array.from([line1, line2, city, state, country, postalCode])
    .flat()
    .filter((d) => d && d !== "")
    .join(", ")
}

const isPoBoxAddress = (address?: IAddress) =>
  /\b(?:[Pp]\.?\s*[Oo]\.?|post\s+office)(\s+)?(?:[Bb]ox|[0-9]*)?\b/g.test(address?.line1 ?? "")

const getAddressSchema = () => {
  return Yup.object().shape({
    line1: Yup.string().required("First line of the address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    postalCode: Yup.string().required("ZIP is required"),
  })
}

const fhirAddressToIAddress = (fhirAddress: FhirAddress[]): IAddress[] =>
  fhirAddress.map((address) => ({
    use: address.use ?? "",
    line1: address.line?.[0] ?? "",
    line2: address.line?.[1] ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
    country: address.country ?? "",
    postalCode: address.postalCode ?? "",
  }))

export {
  getEmails,
  getPhones,
  EMAIL_CONTACT_POINT_SYSTEM,
  PHONE_CONTACT_POINT_SYSTEM,
  genders,
  emailValidation,
  getInitialTelecomErrors,
  getStringAddress,
  isPoBoxAddress,
  getAddressSchema,
  fhirAddressToIAddress,
}
